/**
 * Stations — station list, add/edit form and search picker.
 *
 * Extracted from the old src/components/Settings.js (the component was named
 * `Settings` but the page has always been Stations). Logic is unchanged; only
 * the component name and the import paths were updated for this location.
 */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Card } from 'react-bootstrap';
import { Autocomplete } from '@material-ui/lab';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { hasGlobalStations, allowedSerials } from '../components/roleAccess';
import './Stations.css';
import { API_BASE } from '../config';
import { tr } from '../i18n';

// Roles 1, 4 and 5 open every station's automation interface with a link the
// SERVER builds (GET /auth/automation_link, src/auth/station_autologin.py).
// The auto-login account and key live only in the server's .env - nothing
// secret is in this file or in the JavaScript the browser downloads.

// Some fields come back from the backend as the literal string "None"
// (or "null"/"undefined") instead of a real null when nothing was
// entered - a plain `value || ''` fallback doesn't catch these since
// "None" is a truthy, non-empty string. This treats all of those the
// same as genuinely empty.
const orEmpty = (v) => (v == null || v === 'None' || v === 'null' || v === 'undefined' ? '' : v);
const orZero  = (v) => (v == null || v === 'None' || v === 'null' || v === 'undefined' || v === '' ? 0 : v);

// Zone holds the station's map location for the Station Map page, written the
// way Google Maps gives a Plus Code: "672J+V7 Dar es Salaam". A full code
// (6G5X672J+V7) or coordinates (-6.797813, 39.280688) also work. This is only
// a quick format check while typing - station_map.py on the server does the
// real decoding (and lists any station it cannot place on the map page).
const ZONE_PLUS_CODE = /(?:^|[\s,،])([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,})(?=[\s,،]|$)/i;
const ZONE_COORDS = /^\s*-?\d{1,2}\.\d+\s*[,\s]\s*-?\d{1,3}\.\d+\s*$/;

// district / region: the station's own fields - used by the server when the
// Zone names no town, only the country (Google gives "P8P9+XJJ, Tanzania" for
// places with no town nearby).
const checkZone = (zone, district, region) => {
  const z = String(zone || '').trim();
  if (!z) return { ok: true, text: tr("Map location: copy the Plus Code from Google Maps with the town, e.g. 672J+V7 Dar es Salaam") };
  if (ZONE_COORDS.test(z)) return { ok: true, text: tr("Coordinates - OK for the map.") };
  const m = z.match(ZONE_PLUS_CODE);
  if (!m) return { ok: false, text: tr("No Plus Code found - this station will not show on the map. Example: 672J+V7 Dar es Salaam") };
  const code = m[1].toUpperCase();
  if (code.indexOf('+') === 8) return { ok: true, text: tr("Full Plus Code {0} - OK for the map.", code) };
  const town = z.replace(m[1], ' ')
    .replace(/[,،\s]*(tanzania|kenya|syria|سوريا|سورية)[,،\s]*$/i, '')
    .replace(/^[,،\s]+|[,،\s]+$/g, '');
  if (!town) {
    const near = [orEmpty(district), orEmpty(region)].filter(Boolean).join(' / ');
    if (!near) return { ok: false, text: tr("Add the town after the code, e.g. {0} Dar es Salaam - or fill in the station's Region.", code) };
    return { ok: true, text: tr("Plus Code {0} with no town: placed using the station's District/Region ({1}). Right only if the station is within ~50 km of that town - otherwise add the nearest town after the code.", code, near) };
  }
  return { ok: true, text: tr("Plus Code {0} near {1} - OK for the map.", code, town) };
};

const zoneMapUrl = (zone) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(zone || '').trim())}`;

const StationManagementModal = ({ show, onHide, station, isEdit, onStationSaved }) => {
  const [formData, setFormData] = useState({
    Distributor_Id: 0,
    APISourceId: "", 
    RetailStationName: '',
    EWURALicenseNo: '',
    EWURA_URL: '',
    OperatorTin: 0,
    Operator_XTin: '',
    OperatorVrn: '',
    OperatorUIN: '',
    OperatorName: '',
    LicenseeTraSerialNo: '',
    RegionName: '',
    DistrictName: '',
    WardName: '',
    Zone: '',
    ContactPersonEmailAddress: '',
    ContactPersonPhone: '',
    default_printer_IP: '',
    tank_alarm_volume: 0,
    station_url: '',
    is_vfd: false,
    GeoLocation: null, 
    VFD_provider: '',
    VFD_provider_URL: '',
    VFD_provider_userName: '',
    VFD_provider_userPass: '',
    VFD_provider_TAPIkey: '',
    Tax_office: '',
    automation_server_url: '',
    automation_server_username: '',
    automation_server_pass: '',
    TotalNoTanks: 0,
    x_active_business: '',
    mobile_group1: [],
    mobile_group2: []
  });

  useEffect(() => {
    if (station && isEdit) {
      setFormData({
        Distributor_Id: orZero(station.Distributor_Id),
        APISourceId: orEmpty(station.APISourceId),
        RetailStationName: orEmpty(station.RetailStationName),
        EWURALicenseNo: orEmpty(station.EWURALicenseNo),
        EWURA_URL: orEmpty(station.EWURA_URL),
        OperatorTin: orZero(station.OperatorTin),
        Operator_XTin: orEmpty(station.Operator_XTin),
        OperatorVrn: orEmpty(station.OperatorVrn),
        OperatorUIN: orEmpty(station.OperatorUIN),
        OperatorName: orEmpty(station.OperatorName),
        LicenseeTraSerialNo: orEmpty(station.LicenseeTraSerialNo),
        RegionName: orEmpty(station.RegionName),
        DistrictName: orEmpty(station.DistrictName),
        WardName: orEmpty(station.WardName),
        Zone: orEmpty(station.Zone),
        ContactPersonEmailAddress: orEmpty(station.ContactPersonEmailAddress),
        ContactPersonPhone: orEmpty(station.ContactPersonPhone),
        default_printer_IP: orEmpty(station.default_printer_IP),
        tank_alarm_volume: orZero(station.tank_alarm_volume),
        station_url: orEmpty(station.station_url),
        is_vfd: !!station.is_vfd,
        GeoLocation: station.GeoLocation || null, 
        VFD_provider: orEmpty(station.VFD_provider),
        VFD_provider_URL: orEmpty(station.VFD_provider_URL),
        VFD_provider_userName: orEmpty(station.VFD_provider_userName),
        VFD_provider_userPass: orEmpty(station.VFD_provider_userPass),
        VFD_provider_TAPIkey: orEmpty(station.VFD_provider_TAPIkey),
        Tax_office: orEmpty(station.Tax_office),
        automation_server_url: orEmpty(station.automation_server_url),
        automation_server_username: orEmpty(station.automation_server_username),
        // Never pre-fill with the existing (already-hashed) value - doing
        // so meant every edit re-hashed an already-hashed string, silently
        // corrupting it a little more each time. Left blank means "don't
        // change the password" (the backend already skips hashing when
        // this is empty).
        automation_server_pass: '',
        TotalNoTanks: orZero(station.TotalNoTanks),
        x_active_business: orEmpty(station.x_active_business),
        mobile_group1: Array.isArray(station.mobile_group1) ? station.mobile_group1 : [],
        mobile_group2: Array.isArray(station.mobile_group2) ? station.mobile_group2 : []
      });
    } else if (!isEdit) {
      setFormData({
        Distributor_Id: 0,
        APISourceId: "", 
        RetailStationName: '',
        EWURALicenseNo: '',
        EWURA_URL: '',
        OperatorTin: 0,
        Operator_XTin: '',
        OperatorVrn: '',
        OperatorUIN: '',
        OperatorName: '',
        LicenseeTraSerialNo: '',
        RegionName: '',
        DistrictName: '',
        WardName: '',
        Zone: '',
        ContactPersonEmailAddress: '',
        ContactPersonPhone: '',
        default_printer_IP: '',
        tank_alarm_volume: 0,
        station_url: '',
        is_vfd: false,
        GeoLocation: null, 
        VFD_provider: '',
        VFD_provider_URL: '',
        VFD_provider_userName: '',
        VFD_provider_userPass: '',
        VFD_provider_TAPIkey: '',
        Tax_office: '',
        automation_server_url: '',
        automation_server_username: '',
        automation_server_pass: '',
        TotalNoTanks: 0,
        x_active_business: '',
        mobile_group1: [],
        mobile_group2: []
      });
    }
  }, [station, isEdit]);

  const [loading, setLoading] = useState(false);
  const zoneCheck = checkZone(formData.Zone, formData.DistrictName, formData.RegionName);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleArrayChange = (e, arrayName) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      [`${arrayName}_display`]: value, 
      [arrayName]: value.split(',').map(item => item.trim()).filter(item => item)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // A serial rename is not an ordinary field edit: sales_report alone can
    // hold millions of rows pointing at the old value, and every one is
    // rewritten. Ask first, with the real number rather than a vague warning.
    if (isEdit && station && formData.LicenseeTraSerialNo !== station.LicenseeTraSerialNo) {
      let impact = null;
      try {
        const r = await fetch(`${API_BASE}/stationinfo/${station.id}/serial-impact`, {
          headers: {
            Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token,
          },
        });
        if (r.ok) impact = await r.json();
      } catch (err) {
        // The count is a courtesy — losing it should not block the edit.
      }

      const lines = impact && impact.rows
        ? Object.entries(impact.rows)
            .map(([k, v]) => `<li>${tr('<b>{0}</b> in {1}', Number(v).toLocaleString(), k)}</li>`)
            .join('')
        : '';

      const confirmed = await Swal.fire({
        title: tr("Change the station serial?"),
        html:
          `<p style="margin-bottom:.5rem"><b>${station.LicenseeTraSerialNo}</b> &rarr; <b>${formData.LicenseeTraSerialNo}</b></p>${(lines
            ? `<p style="margin:.5rem 0">${tr("These records will be moved to the new serial:")}</p><ul style="text-align:left;display:inline-block;margin:0">${lines}</ul>`
            : `<p>${tr("No existing records reference the current serial.")}</p>`)}<p style="margin-top:.75rem;font-size:.9em">${tr("This can take a while and cannot be undone from here.")}</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: tr("Yes, change it"),
        cancelButtonText: tr("Cancel"),
        confirmButtonColor: '#e2211c',
      });

      if (!confirmed.isConfirmed) return;
    }

    setLoading(true);

    try {
      const url = isEdit 
        ? `${API_BASE}/stationinfo/${station.id}`
        : `${API_BASE}/stationinfo/`;
      
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
        },
        body: JSON.stringify(formData)
      });

      const body = await response.json().catch(() => ({}));

      if (response.status === 200 || response.status === 201) {
        const rename = body.serial_rename;
        Swal.fire({
          title: tr("Success!"),
          text: rename
            ? tr("Station updated. {0} record(s) moved to {1}.", Number(rename.total_rows).toLocaleString(), rename.to)
            : tr("Station {0} successfully.", isEdit ? tr("updated") : tr("created")),
          icon: 'success',
          confirmButtonText: tr("OK")
        });
        onStationSaved();
        onHide();
      } else if (response.status === 400) {
        Swal.fire({
          title: tr("Validation Error"),
          text: body?.error || body?.message || tr("Please check your input data."),
          icon: 'error',
          confirmButtonText: tr("OK")
        });
      } else if (response.status === 404) {
        Swal.fire({
          title: tr("Error"),
          text: tr("Station not found."),
          icon: 'error',
          confirmButtonText: tr("OK")
        });
      } else {
        Swal.fire({
          title: tr("Error"),
          text: body?.error || body?.message || tr("Request failed ({0}).", response.status),
          icon: 'error',
          confirmButtonText: tr("OK")
        });
      }
    } catch (error) {
      Swal.fire({
        title: tr("Error!"),
        text: tr("Connection problem."),
        icon: 'error',
        confirmButtonText: tr("OK")
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? tr("Edit Station") : tr("Add New Station")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Station Name *")}</Form.Label>
                <Form.Control
                  type="text"
                  name="RetailStationName"
                  value={formData.RetailStationName}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("EWURA License No *")}</Form.Label>
                <Form.Control
                  type="text"
                  name="EWURALicenseNo"
                  value={formData.EWURALicenseNo}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Distributor ID *")}</Form.Label>
                <Form.Control
                  type="number"
                  name="Distributor_Id"
                  value={formData.Distributor_Id}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Licensee Serial No *")}</Form.Label>
                <Form.Control
                  type="text"
                  name="LicenseeTraSerialNo"
                  value={formData.LicenseeTraSerialNo}
                  onChange={handleInputChange}
                  required
                />
                {isEdit && formData.LicenseeTraSerialNo !== station?.LicenseeTraSerialNo && (
                  <Form.Text className="text-warning">
                    {tr("Changing this rewrites every sales, card and tank record that references the old serial.")}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Operator Name")}</Form.Label>
                <Form.Control
                  type="text"
                  name="OperatorName"
                  value={formData.OperatorName}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Operator TIN")}</Form.Label>
                <Form.Control
                  type="number"
                  name="OperatorTin"
                  value={formData.OperatorTin}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Operator X TIN")}</Form.Label>
                <Form.Control
                  type="text"
                  name="Operator_XTin"
                  value={formData.Operator_XTin}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Operator VRN")}</Form.Label>
                <Form.Control
                  type="text"
                  name="OperatorVrn"
                  value={formData.OperatorVrn}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Operator UIN")}</Form.Label>
                <Form.Control
                  type="text"
                  name="OperatorUIN"
                  value={formData.OperatorUIN}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Region")}</Form.Label>
                <Form.Control
                  type="text"
                  name="RegionName"
                  value={formData.RegionName}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("District")}</Form.Label>
                <Form.Control
                  type="text"
                  name="DistrictName"
                  value={formData.DistrictName}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Ward")}</Form.Label>
                <Form.Control
                  type="text"
                  name="WardName"
                  value={formData.WardName}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Zone (map location)")}</Form.Label>
                <Form.Control
                  type="text"
                  name="Zone"
                  value={formData.Zone}
                  onChange={handleInputChange}
                  placeholder="672J+V7 Dar es Salaam"
                />
                <Form.Text className={zoneCheck.ok ? 'text-muted' : 'text-warning'}>
                  {zoneCheck.text}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-center">
              {formData.Zone.trim() && (
                <a href={zoneMapUrl(formData.Zone)} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm">
                  <i className="fas fa-map-marker-alt"></i> {tr("Check in Google Maps")}
                </a>
              )}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Contact Email")}</Form.Label>
                <Form.Control
                  type="email"
                  name="ContactPersonEmailAddress"
                  value={formData.ContactPersonEmailAddress}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Contact Phone")}</Form.Label>
                <Form.Control
                  type="tel"
                  name="ContactPersonPhone"
                  value={formData.ContactPersonPhone}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Tank Alarm Volume")}</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  name="tank_alarm_volume"
                  value={formData.tank_alarm_volume}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  {tr("Litres. An alarm is raised below this level.")}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Station URL")}</Form.Label>
                <Form.Control
                  type="text"
                  name="station_url"
                  value={formData.station_url}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Default Printer IP")}</Form.Label>
                <Form.Control
                  type="text"
                  name="default_printer_IP"
                  value={formData.default_printer_IP}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3 d-flex align-items-end" style={{ height: '100%' }}>
                <Form.Check
                  type="checkbox"
                  id="is_vfd"
                  name="is_vfd"
                  label={tr("Is VFD Enabled")}
                  checked={formData.is_vfd}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Total No. Tanks")}</Form.Label>
                <Form.Control
                  type="number"
                  name="TotalNoTanks"
                  value={formData.TotalNoTanks}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* VFD Provider Information, Tax Office, and Active Business are
              hidden from the form - not editable through this UI anymore.
              Their values are still kept in formData/state as-is (whatever
              the station already has) and submitted unchanged, so hiding
              these fields doesn't wipe out existing data on save. */}

          <hr />
          <h5>{tr("Mobile Groups (comma-separated)")}</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Mobile Group 1")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.mobile_group1_display || formData.mobile_group1.join(', ')}
                  onChange={(e) => handleArrayChange(e, 'mobile_group1')}
                  placeholder={tr("Enter mobile numbers separated by commas")}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Mobile Group 2")}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.mobile_group2_display || formData.mobile_group2.join(', ')}
                  onChange={(e) => handleArrayChange(e, 'mobile_group2')}
                  placeholder={tr("Enter mobile numbers separated by commas")}
                />
              </Form.Group>
            </Col>
          </Row>

          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>
              {tr("Cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? tr("Saving...") : (isEdit ? tr("Update Station") : tr("Add Station"))}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

/* ========================================================
   المكون المعدل بالكامل: بنفس آلية وهيكل وتصميم ملف Users.js
   ======================================================== */
const StationSearchModal = ({ show, onHide, onStationFound, stations }) => {
  const [pickerValue, setPickerValue] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSelectChange = async (e) => {
    const value = e.target.value;
    setPickerValue(value);
    
    if (!value) return;

    setLoading(true);
    try {
      // إرسال الكائن بنفس هيكل ومفاتيح البحث المتوقعة في السيرفر لبيانات المحطة
      const response = await fetch(`${API_BASE}/stationinfo/info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
        },
        body: JSON.stringify({ RetailStationName: value })
      });

      if (response.status === 200) {
        const stationData = await response.json();
        onStationFound(stationData);
        setPickerValue(''); // إعادة تصفير القيمة المحددة بنجاح
        onHide();
      } else if (response.status === 404) {
        Swal.fire({
          title: tr("Not Found"),
          text: tr("Station not found with the provided name."),
          icon: 'warning',
          confirmButtonText: tr("OK")
        });
      }
    } catch (error) {
      Swal.fire({
        title: tr("Error!"),
        text: tr("Connection problem."),
        icon: 'error',
        confirmButtonText: tr("OK")
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{tr("Search Station")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="users-field">
          <label>{tr("Choose a station to search")}</label>
          <select
            className="users-input"
            value={pickerValue}
            onChange={handleSelectChange}
            disabled={loading}
          >
            <option value="">
              {loading ? tr("Searching...") : tr("Choose a station…")}
            </option>
            {stations && stations.map((s) => (
              <option key={s.id || s.LicenseeTraSerialNo} value={s.RetailStationName}>
                {s.RetailStationName} ({s.LicenseeTraSerialNo})
              </option>
            ))}
          </select>
          <div className="users-hint">
            {tr("Only registered stations are accepted — select a station to retrieve and modify its information.")}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {tr("Cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const Stations = ({ token }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentStation, setCurrentStation] = useState(null);
  const [allStations, setAllStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStationName, setSelectedStationName] = useState('');
  const [stationFilter, setStationFilter] = useState(''); // LicenseeTraSerialNo of selected station, '' = all

  const stationBound = !hasGlobalStations(token);
  const allowed = allowedSerials(token); 
  const ssoPayload = (() => { try { return sessionStorage.getItem('sso'); } catch (e) { return null; } })();
  // Roles that open ANY station's automation interface through the server's
  // auto-login link: 1, 4 and 5 - the GLOBAL roles (not bound to a station
  // list, same as hasGlobalStations). The server checks the role again.
  //
  // A role NOT in this list is not locked out: it falls through to the second
  // branch of automationHref() below, which forwards that user's own
  // unmodified sso payload, and only for stations they are allowed to see.
  // So widening this list grants cross-station access, not access as such —
  // that is the thing to weigh before adding a role to it.
  const AUTOLOGIN_ROLES = [1, 4, 5];
  const isGlobalAdmin = AUTOLOGIN_ROLES.includes(Number(token?.user_record?.roles?.id));

  // Other roles: their own sign-in, only for the stations they may see.
  const automationHref = (station) => {
    const url = station.station_url;
    if (!url) return url;
    const payload = (!isGlobalAdmin && stationBound && ssoPayload && (!allowed || allowed.includes(station.LicenseeTraSerialNo)))
      ? ssoPayload : null;
    if (!payload) return url;
    const [base, hash = ''] = url.split('#');
    const sep = hash.includes('?') ? '&' : '?';
    const newHash = hash
      ? `${hash}${sep}sso=${encodeURIComponent(payload)}`
      : `/?sso=${encodeURIComponent(payload)}`;
    return `${base}#${newHash}`;
  };

  // Roles 1/4/5: ask the server for the auto-login link, then open it. The tab
  // is opened straight away (inside the click) so the browser does not block
  // it as a pop-up, and pointed at the link once the server answers.
  const openAutomation = async (event, station) => {
    if (!isGlobalAdmin) return; // normal link (automationHref)
    event.preventDefault();
    const win = window.open('about:blank', '_blank');
    if (win) win.opener = null;
    try {
      const res = await fetch(
        `${API_BASE}/auth/automation_link?serial=${encodeURIComponent(station.LicenseeTraSerialNo)}`,
        { headers: { Authorization: JSON.parse(sessionStorage.getItem('token')).data.access_token } }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) throw new Error(body.message || `HTTP ${res.status}`);
      if (win) win.location.replace(body.url);
      else window.location.assign(body.url);
    } catch (err) {
      if (win) win.close();
      Swal.fire({
        title: tr("Error"),
        text: tr("Could not open the automation interface: {0}", err.message || err),
        icon: 'error',
        confirmButtonText: tr("OK"),
      });
    }
  };

  useEffect(() => {
    fetchAllStations();
  }, []);

  const fetchAllStations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/stationinfo/all`, {
        method: 'GET',
        headers: {
          'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
        }
      });
  
      if (response.status === 200) {
        const result = await response.json();
        const stations = result.data || [];
        setAllStations(stations);
      } else {
        Swal.fire({
          title: tr("Error"),
          text: tr("Failed to fetch stations."),
          icon: 'error',
          confirmButtonText: tr("OK")
        });
      }
    } catch (error) {
      Swal.fire({
        title: tr("Error!"),
        text: tr("Connection problem."),
        icon: 'error',
        confirmButtonText: tr("OK")
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStationFound = (station) => {
    setCurrentStation(station);
    setShowEditModal(true);
  };

  const handleStationSaved = () => {
    fetchAllStations();
    setCurrentStation(null);
  };

  const handleEditStation = (station) => {
    setCurrentStation(station);
    setShowEditModal(true);
  };

  const handleSelectStation = (station) => {
    // This used to stash the station's LAN address for the tank Add/Edit/Delete
    // modals, which posted to `https://${sessionStorage.PumpIp}/v1/tank/`. Those
    // modals went with the pump code, so the IP has no consumer left — only the
    // id and name are kept, which is all the UI still shows.
    setSelectedStationName(station.RetailStationName);
    sessionStorage.setItem('selectedStationId', station.id);
    sessionStorage.setItem('selectedStationName', station.RetailStationName);

    Swal.fire({
      title: tr("Station Selected!"),
      text: tr("Selected: {0}", station.RetailStationName),
      icon: 'success',
      confirmButtonText: tr("OK")
    });
  };

  const handleDeleteStation = async (stationId) => {
    const result = await Swal.fire({
      title: tr("Are you sure?"),
      text: tr("This will permanently delete the station!"),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: tr("Yes, delete it!")
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_BASE}/stationinfo/${stationId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': JSON.parse(sessionStorage.getItem('token')).data.access_token
          }
        });

        if (response.status === 200) {
          Swal.fire({
            title: tr("Deleted!"),
            text: tr("Station has been deleted."),
            icon: 'success',
            confirmButtonText: tr("OK")
          });
          fetchAllStations(); 
        } else if (response.status === 404) {
          Swal.fire({
            title: tr("Not Found"),
            text: tr("Station not found."),
            icon: 'error',
            confirmButtonText: tr("OK")
          });
        }
      } catch (error) {
        Swal.fire({
          title: tr("Error!"),
          text: tr("Connection problem."),
          icon: 'error',
          confirmButtonText: tr("OK")
        });
      }
    }
  };

  const filteredStations = stationFilter
    ? allStations.filter(s => s.LicenseeTraSerialNo === stationFilter)
    : allStations;

  // Split the list into the two station types. The backend has sent is_vfd as
  // a real boolean, as 1/0, and as the strings "1"/"true" depending on the
  // endpoint and driver, so normalise rather than testing truthiness — the
  // string "0" is truthy in JS and would file every ATG station under VFD.
  const isVfdStation = (s) => {
    const v = s.is_vfd;
    if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
    return v === 1 || v === true;
  };

  const vfdStations = filteredStations.filter(isVfdStation);
  const atgStations = filteredStations.filter((s) => !isVfdStation(s));

  // One definition of a station card, rendered by both sectors.
  const renderStationCard = (station) => (
                  <Card key={station.id} className="station-card">
                    <Card.Header className="station-card-header">
                      <div className="station-title">
                        <h5>{station.RetailStationName}</h5>
                        <small>{station.EWURALicenseNo}</small>
                      </div>
                      <div className="station-actions">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSelectStation(station)}
                          title={tr("Select this station")}
                        >
                          <i className="fas fa-check"></i>
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleEditStation(station)}
                          title={tr("Edit station")}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteStation(station.id)}
                          title={tr("Delete station")}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {station.station_url ? (
                        <div className="station-link mb-2">
                          <a
                            href={automationHref(station)}
                            onClick={(e) => openAutomation(e, station)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm station-link-btn"
                            title={station.station_url}
                          >
                            <i className="fas fa-external-link-alt"></i> {tr("Open Automation Interface")}
                          </a>
                          <br />
                          <small className="station-link-url">{station.station_url}</small>
                        </div>
                      ) : (
                        <div className="station-link mb-2">
                          <small className="text-muted"><i className="fas fa-unlink"></i> {tr("No automation link set")}</small>
                        </div>
                      )}
                      <Row>
                        <Col md={6}>
                          <small><strong>{tr("Operator:")}</strong> {station.OperatorName}</small><br />
                          <small><strong>{tr("Serial No:")}</strong> {station.LicenseeTraSerialNo}</small><br />
                          <small><strong>{tr("Region:")}</strong> {station.RegionName}</small><br />
                          <small><strong>{tr("District:")}</strong> {station.DistrictName}</small><br />
                          <small><strong>{tr("Ward:")}</strong> {orEmpty(station.WardName)}</small>
                        </Col>
                        <Col md={6}>
                          <small><strong>{tr("Alarm Volume:")}</strong> {tr("{0} L", Number(station.tank_alarm_volume || 0).toLocaleString())}</small><br />
                          <small><strong>{tr("Contact:")}</strong> {station.ContactPersonEmailAddress}</small><br />
                          <small><strong>{tr("Phone:")}</strong> {station.ContactPersonPhone}</small><br />
                          <small><strong>{tr("Tanks:")}</strong> {station.TotalNoTanks}</small>
                        </Col>
                      </Row>
                      <div className="mt-2">
                        <small>
                          <strong>{tr("Zone:")}</strong>{' '}
                          {orEmpty(station.Zone) ? (
                            <>
                              {station.Zone}{' '}
                              <a href={zoneMapUrl(station.Zone)} target="_blank" rel="noopener noreferrer" title={tr("Check in Google Maps")}>
                                <i className="fas fa-map-marker-alt"></i>
                              </a>
                              {!checkZone(station.Zone, station.DistrictName, station.RegionName).ok && (
                                <span className="text-warning"> ({tr("not on map")})</span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">{tr("not set")}</span>
                          )}
                        </small>
                      </div>
                      {station.GeoLocation && (
                        <div className="mt-2">
                          <small><strong>{tr("Location:")}</strong> {station.GeoLocation.latitude}, {station.GeoLocation.longitude}</small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
  );

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>{tr("Stations")}</h2>
        <p>{tr("Manage fuel stations and open their automation interfaces")}</p>
      </div>

      {selectedStationName && (
        <div className="settings-section">
          <div className="section-header">
            <h4>{tr("Currently Selected Station")}</h4>
            <p><strong>{selectedStationName}</strong></p>
          </div>
        </div>
      )}

      <div className="settings-section">
        <div className="section-header">
          <h4>{tr("Station Management")}</h4>
          <p>{tr("Add, edit, search, and delete fuel stations")}</p>
        </div>

        <div className="action-buttons">
          <Button 
            variant="primary" 
            onClick={() => setShowAddModal(true)}
            className="action-btn"
          >
            <i className="fas fa-plus"></i> {tr("Add New Station")}
          </Button>
          
          <Button 
            variant="info" 
            onClick={() => setShowSearchModal(true)}
            className="action-btn"
          >
            <i className="fas fa-search"></i> {tr("Search & Edit Station")}
          </Button>

          <Button 
            variant="secondary" 
            onClick={fetchAllStations}
            className="action-btn"
            disabled={loading}
          >
            <i className="fas fa-sync-alt"></i> {loading ? tr("Refreshing...") : tr("Refresh List")}
          </Button>
        </div>
      </div>

      <div className="settings-section">
        <div className="section-header">
          <h4>{tr("All Stations ({0})", filteredStations.length)}</h4>
          <p>
            {tr("Select, edit, or delete existing fuel stations")}
            {' — '}
            {tr("{0} VFD, {1} ATG", vfdStations.length, atgStations.length)}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <FormControl style={{ minWidth: 240 }}>
            <Autocomplete
              options={allStations}
              getOptionLabel={s => s.RetailStationName || ''}
              value={allStations.find(s => s.LicenseeTraSerialNo === stationFilter) || null}
              onChange={(e, newValue) => setStationFilter(newValue ? newValue.LicenseeTraSerialNo : '')}
              renderInput={params => (
                <TextField {...params} placeholder={tr("All Stations")} />
              )}
            />
            <FormHelperText>{tr("Station")}</FormHelperText>
          </FormControl>
        </div>

        {loading ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">{tr("Loading...")}</span>
            </div>
          </div>
        ) : (
          <>
            {vfdStations.length > 0 && (
              <section className="station-sector">
                <div className="station-sector-header">
                  <h5>
                    <i className="fas fa-microchip"></i> {tr("VFD Stations")}
                  </h5>
                  <span className="station-sector-count">{vfdStations.length}</span>
                </div>
                <div className="stations-grid">
                  {vfdStations.map((station) => renderStationCard(station))}
                </div>
              </section>
            )}

            {atgStations.length > 0 && (
              <section className="station-sector">
                <div className="station-sector-header">
                  <h5>
                    <i className="fas fa-gas-pump"></i> {tr("ATG Stations")}
                  </h5>
                  <span className="station-sector-count">{atgStations.length}</span>
                </div>
                <div className="stations-grid">
                  {atgStations.map((station) => renderStationCard(station))}
                </div>
              </section>
            )}
          </>
        )}

        {!loading && filteredStations.length === 0 && (
          <Alert variant="info">
            <i className="fas fa-info-circle"></i>{' '}
            {allStations.length === 0
              ? tr("No stations found. Add your first station using the \"Add New Station\" button.")
              : tr("No station matches your search.")}
          </Alert>
        )}
      </div>

      <StationManagementModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        station={null}
        isEdit={false}
        onStationSaved={handleStationSaved}
      />

      {/* الـ Modal المستدعى المحدث */}
      <StationSearchModal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        onStationFound={handleStationFound}
        stations={allStations}
      />

      <StationManagementModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setCurrentStation(null);
        }}
        station={currentStation}
        isEdit={true}
        onStationSaved={handleStationSaved}
      />
    </div>
  );
};

export default Stations;